import { cqiConstants } from 'locale/cqi'
import {
  CQITabID,
  CQI_BASE_URL,
  TablesDataSource,
  getRoundedPercentage,
} from './utils'
import { TFunction, useTranslation } from 'react-i18next'
import BaseCQITable from './BaseCQITable'
import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { NAMESPACE } from 'locale'

const CQI_RESPONSIVE_URL = `${CQI_BASE_URL}/responsive`

export type ResponsiveDataFetched = {
  carePlansComplete: number
  carePlansInDate: number
  eolCarePlans: number
}

type AdditionalInfo = {
  residentsCount: number
}

type ResponsiveTableProps = AdditionalInfo & {
  isLoading: boolean
}

const ResponsiveTable = ({
  isLoading: isLoadingFromProps,
  residentsCount,
}: ResponsiveTableProps) => {
  const { t } = useTranslation(NAMESPACE.CQI)

  const { data, isLoading } = useQuery<{ result: ResponsiveDataFetched }>(
    ['cqi', CQITabID.Responsive],
    () => getJSON({ url: CQI_RESPONSIVE_URL })
  )

  return (
    <BaseCQITable
      tabID={CQITabID.Responsive}
      isLoading={isLoading || isLoadingFromProps}
      dataSource={getResponsiveData(t, data?.result, { residentsCount })}
    />
  )
}

export const getResponsiveData = (
  t: TFunction<string, undefined>,
  props: ResponsiveDataFetched | undefined,
  additionalInfo: AdditionalInfo
): TablesDataSource[] => {
  let supportPlansCompletedPercentage: string | undefined
  let supportPlansInDatePercentage: string | undefined
  let clientsWithEOLSupportPlans: string | undefined

  if (props !== undefined) {
    if (props.carePlansComplete !== undefined) {
      supportPlansCompletedPercentage = getRoundedPercentage(
        props.carePlansComplete
      )
    }

    if (props.carePlansInDate !== undefined) {
      supportPlansInDatePercentage = getRoundedPercentage(props.carePlansInDate)
    }

    if (props.eolCarePlans !== undefined && additionalInfo?.residentsCount) {
      clientsWithEOLSupportPlans = `${props.eolCarePlans}/${additionalInfo.residentsCount}`
    }
  }

  return [
    {
      key: t(cqiConstants.SUPPORT_PLANS_COMPLETED),
      indicator: t(cqiConstants.SUPPORT_PLANS_COMPLETED),
      timeframe: t(cqiConstants.PRESENT_TIME),
      output: supportPlansCompletedPercentage,
    },
    {
      key: t(cqiConstants.SUPPORT_PLANS_IN_DATE),
      indicator: t(cqiConstants.SUPPORT_PLANS_IN_DATE),
      timeframe: t(cqiConstants.PRESENT_TIME),
      output: supportPlansInDatePercentage,
    },
    {
      key: t(cqiConstants.CLIENTS_WITH_EOL_SUPPORT_PLANS),
      indicator: t(cqiConstants.CLIENTS_WITH_EOL_SUPPORT_PLANS),
      timeframe: t(cqiConstants.PRESENT_TIME),
      output: clientsWithEOLSupportPlans,
    },
  ]
}
export default ResponsiveTable
